import { ApiResponse } from 'bff/client';

export type DownloadFileResult = {
  blob: Blob;
  fileName?: string;
};

const parseFileNameFromContentDisposition = (
  contentDisposition: string | null
): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }

  const fileNameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (fileNameStarMatch?.[1]) {
    try {
      return decodeURIComponent(fileNameStarMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return fileNameStarMatch[1].trim().replace(/^"|"$/g, '');
    }
  }

  const fileNameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  return fileNameMatch?.[1]?.trim();
};

/**
 * Attempts to resolve a filename from the `Content-Disposition` response header, then resolves
 * the blob from the response — returning both together as a {@link DownloadFileResult}.
 *
 * Filename resolution order:
 * 1. `filename*` (RFC 5987, UTF-8 encoded) from `Content-Disposition`
 * 2. `filename` (plain) from `Content-Disposition`
 * 3. `fallbackFileName` if the header is absent or contains no parseable filename
 *
 * @param response - The raw API response wrapping the file blob.
 * @param fallbackFileName - Filename to use when no filename can be read from the response headers.
 * @returns A promise resolving to an object containing the blob and the resolved filename.
 */
export const getBlobWithFileName = async (
  response: ApiResponse<Blob>,
  fallbackFileName?: string
): Promise<DownloadFileResult> => {
  const contentDisposition = response.raw.headers.get('content-disposition');
  const blob = await response.value();

  return {
    blob,
    fileName: parseFileNameFromContentDisposition(contentDisposition) ?? fallbackFileName
  };
};
